import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button } from "./Button";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { GlowingEffect } from "../animations/GlowingEffect";
import { cn, isValidUrl } from "../services/Utils";

export function SearchBar({
  className = "",
  placeholder = "yourwebsite.com",
  autoFocus = false,
  onBeforeNavigate,
  variant = "default",
}) {
  const [domain, setDomain] = useState("");
  const navigate = useNavigate();

  // Strip protocol if user pastes a full URL
  const handleDomainChange = (e) => {
    let val = e.target.value;
    if (val.startsWith("https://")) val = val.slice(8);
    else if (val.startsWith("http://")) val = val.slice(7);
    setDomain(val);
  };

  const handleSubmit = () => {
    if (!domain.trim()) {
      toast.error("Please enter a valid URL");
      return;
    }

    const fullUrl = `https://${domain}`;

    if (!isValidUrl(fullUrl)) {
      toast.error(
        "Please enter a valid URL (e.g. yourwebsite.com)",
      );
      return;
    }

    onBeforeNavigate?.();
    navigate(`/get-started?url=${encodeURIComponent(fullUrl)}`);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  // Compact single-row pill used by the landing page hero.
  if (variant === "pill") {
    return (
      <form
        className={cn(
          "relative mx-auto flex h-14 w-full max-w-xl items-center rounded-full border border-neutral-200 bg-white pl-4 pr-1.5 shadow-[0_8px_30px_rgba(0,0,0,0.06)] sm:pl-6",
          className,
        )}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <GlowingEffect
          blur={0}
          borderWidth={1}
          spread={80}
          glow={true}
          disabled={false}
          proximity={400}
          inactiveZone={0.01}
        />
        <span
          className="relative shrink-0 select-none pr-1 text-[13px] text-neutral-400 sm:text-[15px]"
          style={{ fontFamily: "var(--font-inter-tight)" }}
        >
          https://
        </span>
        <input
          className="relative min-w-0 grow border-none bg-transparent text-[13px] text-neutral-900 placeholder:text-neutral-400 focus:border-none focus:outline-none focus:ring-0 sm:text-[15px]"
          style={{ fontFamily: "var(--font-inter-tight)" }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          value={domain}
          onChange={handleDomainChange}
        />
        <button
          type="submit"
          className="relative flex h-11 shrink-0 items-center whitespace-nowrap rounded-full bg-primary px-4 text-[14px] font-semibold text-white transition-colors hover:bg-primary-dark sm:px-5"
        >
          Get Started
          <ArrowRightIcon className="ml-2 size-4" />
        </button>
      </form>
    );
  }

  return (
    <div className={`max-w-3xl mx-auto w-full bg-white ${className}`}>
      <div
        className="relative rounded-xl sm:rounded-full"
        style={{
          borderWidth: "1px",
          borderStyle: "solid",
          borderColor: "var(--color-secondary-light)",
        }}
      >
        <GlowingEffect
          blur={0}
          borderWidth={1}
          spread={80}
          glow={true}
          disabled={false}
          proximity={400}
          inactiveZone={0.01}
        />
        <div className="relative z-10 p-3 sm:p-2.5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center grow ml-4 my-auto">
            <span className="text-gray-400 text-base select-none shrink-0 pr-1">
              https://
            </span>
            <input
              className="grow border-none outline-none focus:outline-none focus:ring-0 focus:border-none h-6"
              placeholder={placeholder}
              autoFocus={autoFocus}
              value={domain}
              onChange={handleDomainChange}
              onKeyDown={handleKeyPress}
            />
          </div>
          <Button
            className="w-full sm:w-auto flex justify-center rounded-full!"
            variant="solid"
            size="lg"
            color="primary"
            onClick={handleSubmit}
          >
            Get Started
            <ArrowRightIcon className="size-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
